import { Component, EventEmitter, forwardRef, HostBinding, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Input s dekorativním prefixem (např. "CZ-") před hodnotou.
 * Prefix je čistě vizuální - není součástí hodnoty ani odesílaných dat.
 * Obdoba PrefixedLabelledEditorFor z Rel.Admin (MVC aplikace).
 */
@Component({
  selector: 'app-prefixed-input',
  templateUrl: './prefixed-input.component.html',
  styleUrls: ['./prefixed-input.component.css'],
  imports: [FormsModule],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PrefixedInputComponent),
    multi: true
  }],
  standalone: true
})
export class PrefixedInputComponent implements ControlValueAccessor {

  @Input() prefix: string = 'CZ-';
  @Input() placeholder: string = '';

  /**
   * Blur vnitřního inputu - native (blur) na tomto komponentu by nefungoval, protože blur
   * nebubluje a skutečný input je uvnitř šablony, ne na hostiteli komponenty.
   */
  @Output() blurred = new EventEmitter<void>();

  private _id = '';
  @HostBinding('attr.id')
  externalId = '';

  @Input()
  set id(value: string) {
    this._id = value;
    this.externalId = null;
  }
  get id() {
    return this._id;
  }

  disabled: boolean = false;
  onModelChange: any = () => { };
  onModelTouched: any = () => { };

  private _value: string = '';
  get inputValue() {
    return this._value;
  }
  set inputValue(val: string) {
    if (val !== this._value) {
      this._value = val;
      this.onModelChange(val);
    }
  }

  writeValue(value: any): void {
    this._value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Volané při blur vnitřního inputu. Angular pro updateOn:'blur' nepoužívá nativní blur
   * listener na hostiteli (nemohl by fungovat, blur nebubluje) - spoléhá na to, že
   * ControlValueAccessor sám zavolá registrovaný onTouched callback (viz setUpBlurPipeline
   * v @angular/forms). Bez tohoto volání by se pole při prázdném blur nikdy neoznačilo jako
   * touched a nezobrazila by se chybová stylizace.
   */
  onBlur(): void {
    this.onModelTouched();
    this.blurred.emit();
  }

  // Odřízne prefix při vložení ze schránky - stejné chování jako
  // Pds.Rel.InitPrefixedEditorPaste v MVC aplikaci (Rel.js).
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const esc = this.prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*');
    const cleaned = pasted.replace(new RegExp('^\\s*' + esc, 'i'), '');
    if (cleaned === pasted) return;

    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const newValue = this._value.slice(0, start) + cleaned + this._value.slice(end);
    this.inputValue = newValue;

    setTimeout(() => input.setSelectionRange(start + cleaned.length, start + cleaned.length));
  }
}
